import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Library } from '../types';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC','PR','GU','VI','AS','MP',
];

const KNOWN_ILS_VENDORS = [
  'Koha', 'Evergreen', 'SirsiDynix', 'Innovative (Sierra/Millennium)',
  'CARL.X', 'Apollo/Biblionix', 'Atriuum', 'Polaris', 'TLC', 'Other',
];

interface SystemOption {
  id: number;
  name: string;
  system_name: string | null;
  city: string;
  state: string;
}

interface FormData {
  parent_id: string;
  name: string;
  system_name: string;
  branch_level: string;
  city: string;
  state: string;
  region: string;
  lat: string;
  lng: string;
  ils_vendor: string;
  z3950_host: string;
  z3950_port: string;
  z3950_database: string;
  sru_base_url: string;
  sru_record_schema: string;
  oaipmh_base_url: string;
  oaipmh_metadata_prefix: string;
  oaipmh_set: string;
  opac_url: string;
  ingest_method: string;
  status: string;
  confidence: string;
  notes: string;
}

const emptyForm: FormData = {
  parent_id: '', name: '', system_name: '', branch_level: 'system', city: '', state: '',
  region: '', lat: '', lng: '', ils_vendor: '', z3950_host: '', z3950_port: '210',
  z3950_database: '', sru_base_url: '', sru_record_schema: 'marcxml',
  oaipmh_base_url: '', oaipmh_metadata_prefix: 'marc21', oaipmh_set: '',
  opac_url: '', ingest_method: 'manual', status: 'needs_review',
  confidence: 'provisional', notes: '',
};

export default function LibraryManage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState<FormData>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Parent system options
  const [systems, setSystems] = useState<SystemOption[]>([]);

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    api.get<{ data: SystemOption[] }>('/libraries/systems')
      .then((res) => setSystems(res.data))
      .catch(() => {}); // non-critical

    if (isEdit) {
      setLoading(true);
      api.get<{ data: Library }>(`/libraries/${id}`)
        .then((res) => {
          const lib = res.data;
          setForm({
            parent_id: lib.parent_id != null ? String(lib.parent_id) : '',
            name: lib.name,
            system_name: lib.system_name || '',
            branch_level: lib.branch_level,
            city: lib.city,
            state: lib.state,
            region: lib.region || '',
            lat: lib.lat != null ? String(lib.lat) : '',
            lng: lib.lng != null ? String(lib.lng) : '',
            ils_vendor: lib.ils_vendor || '',
            z3950_host: lib.z3950_host || '',
            z3950_port: lib.z3950_port != null ? String(lib.z3950_port) : '210',
            z3950_database: lib.z3950_database || '',
            sru_base_url: lib.sru_base_url || '',
            sru_record_schema: lib.sru_record_schema || 'marcxml',
            oaipmh_base_url: lib.oaipmh_base_url || '',
            oaipmh_metadata_prefix: lib.oaipmh_metadata_prefix || 'marc21',
            oaipmh_set: lib.oaipmh_set || '',
            opac_url: lib.opac_url || '',
            ingest_method: lib.ingest_method,
            status: lib.status,
            confidence: lib.confidence,
            notes: lib.notes || '',
          });
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const set = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError(null);
    setSuccess(null);
  };

  const buildPayload = () => {
    const payload: Record<string, unknown> = { ...form };
    // Convert numeric fields
    if (form.parent_id) payload.parent_id = parseInt(form.parent_id, 10);
    else payload.parent_id = null;
    if (form.lat) payload.lat = parseFloat(form.lat);
    else payload.lat = null;
    if (form.lng) payload.lng = parseFloat(form.lng);
    else payload.lng = null;
    if (form.z3950_port) payload.z3950_port = parseInt(form.z3950_port, 10);
    else payload.z3950_port = null;
    // Clear empty strings to null for nullable fields
    for (const key of ['system_name', 'region', 'ils_vendor', 'z3950_host',
      'z3950_database', 'sru_base_url', 'sru_record_schema',
      'oaipmh_base_url', 'oaipmh_metadata_prefix', 'oaipmh_set',
      'opac_url', 'notes']) {
      if (!payload[key]) payload[key] = null;
    }
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (isEdit) {
        await api.put(`/libraries/${id}`, buildPayload());
        setSuccess('Library updated.');
      } else {
        const res = await api.post<{ data: Library }>('/libraries', buildPayload());
        setSuccess('Library created.');
        navigate(`/libraries/${res.data.id}/edit`, { replace: true });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    const method = form.ingest_method === 'sru' ? 'sru'
      : form.ingest_method === 'oaipmh' ? 'oaipmh'
      : 'z3950';
    try {
      const result = await api.post<{ success: boolean; message: string }>(
        '/libraries/test-connection',
        {
          method,
          z3950_host: form.z3950_host,
          z3950_port: parseInt(form.z3950_port, 10),
          z3950_database: form.z3950_database,
          sru_base_url: form.sru_base_url,
          oaipmh_base_url: form.oaipmh_base_url,
        }
      );
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  const canTestConnection =
    (form.ingest_method === 'z3950' && form.z3950_host && form.z3950_port && form.z3950_database) ||
    (form.ingest_method === 'sru' && form.sru_base_url) ||
    (form.ingest_method === 'oaipmh' && form.oaipmh_base_url);

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <h1>{isEdit ? 'Edit Library' : 'Add Library'}</h1>

      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}

      <form onSubmit={handleSubmit}>
        {/* Basic info */}
        <fieldset>
          <legend>Basic Information</legend>
          <div className="form-row">
            <label>Name *<input type="text" value={form.name} onChange={set('name')} required /></label>
            <label>System Name<input type="text" value={form.system_name} onChange={set('system_name')} placeholder="e.g. County Library System" /></label>
          </div>
          {form.branch_level !== 'system' && systems.length > 0 && (
            <div className="form-row">
              <label>Parent System
                <select value={form.parent_id} onChange={set('parent_id')}>
                  <option value="">None (independent)</option>
                  {systems.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}{s.system_name ? ` (${s.system_name})` : ''} — {s.city}, {s.state}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <div className="form-row">
            <label>City *<input type="text" value={form.city} onChange={set('city')} required /></label>
            <label>State *
              <select value={form.state} onChange={set('state')} required>
                <option value="">Select...</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label>Region<input type="text" value={form.region} onChange={set('region')} placeholder="e.g. Midwest" /></label>
          </div>
          <div className="form-row">
            <label>Branch Level
              <select value={form.branch_level} onChange={set('branch_level')}>
                <option value="system">System</option>
                <option value="branch">Branch</option>
                <option value="sub-branch">Sub-branch</option>
              </select>
            </label>
            <label>Latitude<input type="text" value={form.lat} onChange={set('lat')} placeholder="e.g. 40.7128" /></label>
            <label>Longitude<input type="text" value={form.lng} onChange={set('lng')} placeholder="e.g. -74.0060" /></label>
          </div>
        </fieldset>

        {/* Connection info */}
        <fieldset>
          <legend>Catalog Connection</legend>
          <div className="form-row">
            <label>ILS Vendor
              <select value={form.ils_vendor} onChange={set('ils_vendor')}>
                <option value="">Unknown</option>
                {KNOWN_ILS_VENDORS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label>Ingest Method
              <select value={form.ingest_method} onChange={set('ingest_method')}>
                <option value="z3950">Z39.50</option>
                <option value="sru">SRU</option>
                <option value="oaipmh">OAI-PMH</option>
                <option value="opac_scrape">OPAC Scrape</option>
                <option value="manual">Manual / Not Set</option>
              </select>
            </label>
          </div>

          {form.ingest_method === 'z3950' && (
            <div className="form-row">
              <label>Z39.50 Host<input type="text" value={form.z3950_host} onChange={set('z3950_host')} placeholder="e.g. z3950.loc.gov" /></label>
              <label>Port<input type="number" value={form.z3950_port} onChange={set('z3950_port')} placeholder="210" style={{ width: '6rem' }} /></label>
              <label>Database<input type="text" value={form.z3950_database} onChange={set('z3950_database')} placeholder="e.g. VOYAGER" /></label>
            </div>
          )}

          {form.ingest_method === 'sru' && (
            <div className="form-row">
              <label>SRU Base URL<input type="url" value={form.sru_base_url} onChange={set('sru_base_url')} placeholder="https://catalog.example.org/sru" style={{ minWidth: '20rem' }} /></label>
              <label>Record Schema
                <select value={form.sru_record_schema} onChange={set('sru_record_schema')}>
                  <option value="marcxml">MARCXML</option>
                  <option value="dc">Dublin Core</option>
                  <option value="mods">MODS</option>
                </select>
              </label>
            </div>
          )}

          {form.ingest_method === 'opac_scrape' && (
            <div className="form-row">
              <label>OPAC URL<input type="url" value={form.opac_url} onChange={set('opac_url')} placeholder="https://catalog.example-library.org" style={{ minWidth: '20rem' }} /></label>
            </div>
          )}

          {form.ingest_method === 'oaipmh' && (
            <div className="form-row">
              <label>OAI-PMH Base URL<input type="url" value={form.oaipmh_base_url} onChange={set('oaipmh_base_url')} placeholder="https://www.loc.gov/cds/oai" style={{ minWidth: '20rem' }} /></label>
              <label>Metadata Prefix
                <select value={form.oaipmh_metadata_prefix} onChange={set('oaipmh_metadata_prefix')}>
                  <option value="marc21">MARC21</option>
                  <option value="oai_dc">Dublin Core</option>
                  <option value="mods">MODS</option>
                </select>
              </label>
              <label>Set (optional)<input type="text" value={form.oaipmh_set} onChange={set('oaipmh_set')} placeholder="e.g. hdl_2027_spo" /></label>
            </div>
          )}

          {canTestConnection && (
            <div style={{ marginTop: '0.5rem' }}>
              <button type="button" onClick={handleTestConnection} disabled={testing}>
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              {testResult && (
                <span style={{ marginLeft: '1rem', color: testResult.success ? 'green' : 'red' }}>
                  {testResult.message}
                </span>
              )}
            </div>
          )}

          <p className="hint">
            Tip: Look up Z39.50 targets at{' '}
            <a href="https://irspy.indexdata.com" target="_blank" rel="noopener noreferrer">irspy.indexdata.com</a>
            {' '}or check the library's website under "For developers" / "Z39.50 access".
            Many libraries in a consortium share one endpoint.
          </p>
        </fieldset>

        {/* Status */}
        <fieldset>
          <legend>Status</legend>
          <div className="form-row">
            <label>Status
              <select value={form.status} onChange={set('status')}>
                <option value="active">Active</option>
                <option value="needs_review">Needs Review</option>
                <option value="skipped">Skipped</option>
              </select>
            </label>
            <label>Confidence
              <select value={form.confidence} onChange={set('confidence')}>
                <option value="provisional">Provisional</option>
                <option value="confirmed">Confirmed</option>
              </select>
            </label>
          </div>
          <label>Notes<textarea value={form.notes} onChange={set('notes')} rows={3} style={{ width: '100%' }} /></label>
        </fieldset>

        <div style={{ marginTop: '1rem' }}>
          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Library' : 'Create Library'}
          </button>
          <button type="button" onClick={() => navigate('/libraries')} style={{ marginLeft: '0.5rem' }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
